import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../common/AppProvider';
import { translations } from '../translations/translations';
import { useMobile } from '../tasks/useMobileHook';
import AnimatedLink from '../common/AnimatedLink';
import SimpleSlider from '../common/SimpleSlider.tsx';
import { ExButton, ExIcon } from '@boomi/exosphere';

import './new-task-connectors.css';

const NewTaskConnectors = () => {
    const [triggerSelected, setTriggerSelected] = useState<string | undefined>(undefined);
    const [actionsSelected, setActionsSelected] = useState<string[]>([]);

    const { language, connectors } = useApp();
    const { isMobile, portalContainer } = useMobile(800);

    const onActionSelected = (appName: string) => {
        const apps = [...actionsSelected];

        if (apps.includes(appName)) {
            const filteredApps = apps.filter((app) => app !== appName);
            setActionsSelected(filteredApps);
        } else {
            apps.push(appName);
            setActionsSelected(apps);
        }
    };

    const generateURL = () => {
        let urlStr = `trigger=${triggerSelected}&`;

        for (let i = 0; i < actionsSelected.length; i++) {
            // Add all the actions connectors to the URL param and append '&' if there's more to go.
            urlStr += `action=${actionsSelected[i]}${i !== actionsSelected.length - 1 ? '&' : ''}`;
        }

        return urlStr;
    };

    const backNextButtons = () => (
        <div className="onboarding-btn-wrapper">
            <span className="send-request-text text md">
                {translations[language].ONBOARDING_send_request_before}
                <span id="send-a-request" className="btn-text">
                    {translations[language].ONBOARDING_send_request}
                </span>
                {translations[language].ONBOARDING_send_request_after}
            </span>

            <AnimatedLink to="/tasks" tabIndex={-1} className="btn-back">
                <ExButton className="exo-button" flavor="base" type="secondary" size="large">
                    <ExIcon icon="Left arrow" slot="prefix" hide-browser-tooltip></ExIcon>
                    {translations[language].COMMON_back}
                </ExButton>
            </AnimatedLink>

            <AnimatedLink
                to={`/tasks/new/suggestions?${generateURL()}`}
                tabIndex={-1}
                className={`btn-next ${
                    !triggerSelected || !actionsSelected.length ? 'is-disabled' : ''
                }`}
            >
                <ExButton
                    className="exo-button"
                    flavor="base"
                    type="primary"
                    size="large"
                    disabled={!triggerSelected || !actionsSelected.length}
                >
                    <ExIcon icon="Right arrow" slot="suffix" hide-browser-tooltip></ExIcon>
                    {translations[language].COMMON_next}
                </ExButton>
            </AnimatedLink>
        </div>
    );

    return (
        <>
            <section className="new-task-start-blank">
                <p className="text md">{translations[language].ONBOARDING_blank_hint}</p>
                <AnimatedLink
                    to="/tasks/new-task"
                    className="new-task-button exo-button-secondary"
                    title={translations[language].ONBOARDING_blank_task_description}
                    role="button"
                >
                    <ExIcon
                        className="new-task-icon"
                        icon="plus"
                        slot="prefix"
                        hide-browser-tooltip
                    ></ExIcon>
                    <span className="new-task-text">{translations[language].TASKS_add_new}</span>
                </AnimatedLink>
            </section>

            <section className="new-task-sliders">
                <div className="simple-slider-card ta-tile">
                    <h2 className="title xxl">{translations[language].ONBOARDING_when_title}</h2>
                    <p className="text md">{translations[language].ONBOARDING_when_hint}</p>
                    <SimpleSlider
                        sliderItems={connectors}
                        itemType="trigger"
                        isMultiSelect={false}
                        selectedItems={triggerSelected ? [connectors[triggerSelected]] : []}
                        onSelect={(connector) => setTriggerSelected(connector.id)}
                        language={language}
                    />
                </div>
                <ExIcon className="slider-icon" icon="Right arrow" hide-browser-tooltip />
                <div className="simple-slider-card ta-tile">
                    <h2 className="title xxl">{translations[language].ONBOARDING_then_title}</h2>
                    <p className="text md">{translations[language].ONBOARDING_then_hint}</p>
                    <SimpleSlider
                        sliderItems={connectors}
                        itemType="action"
                        isMultiSelect={true}
                        selectedItems={actionsSelected.map((id) => connectors[id])}
                        onSelect={(connector) => onActionSelected(connector.id)}
                        language={language}
                    />
                </div>
            </section>

            {/* Portal the buttons into the footer on mobile. This UI behaviour is experimental for now. */}
            {isMobile && portalContainer
                ? createPortal(backNextButtons(), portalContainer)
                : backNextButtons()}
        </>
    );
};

export default NewTaskConnectors;
